// ---------------------------------------------------------------------------
// Secure storage layer
// ---------------------------------------------------------------------------
//
// Unified async storage facade that transparently routes through AppLock's
// encrypted StorageAdapter when a lock instance is active, and falls back to
// plain (cleartext) localStorage otherwise.
//
// All methods are async so callers can `await` them regardless of whether
// encryption is enabled.
// ---------------------------------------------------------------------------

import type { StorageAdapter } from '@ascend/applock';

let lockStore: StorageAdapter | null = null;

export function setLockStore(store: StorageAdapter | null): void {
  lockStore = store;
}

export function getLockStore(): StorageAdapter | null {
  return lockStore;
}

export async function secureSave(key: string, data: unknown): Promise<void> {
  if (lockStore) {
    await lockStore.save(key, data);
  } else {
    localStorage.setItem(key, JSON.stringify(data));
  }
}

export async function secureLoad<T>(key: string): Promise<T | null> {
  if (lockStore) {
    return lockStore.load<T>(key);
  }
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export async function secureRemove(key: string): Promise<void> {
  if (lockStore) {
    await lockStore.remove(key);
  } else {
    localStorage.removeItem(key);
  }
}

export async function secureKeys(): Promise<string[]> {
  if (lockStore) {
    return lockStore.keys();
  }
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    keys.push(localStorage.key(i)!);
  }
  return keys;
}
